import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { getMensajesDeConversacion } from '../../services/conversacionesService.js';
import { supabase } from '../../services/supabase/supabaseClient.js';

const looksLikeHtml = (str) => typeof str === 'string' && /<\/?[a-z][\s\S]*>/i.test(str);

// Devuelve { html } si el contenido debe renderizarse como HTML (emails),
// o { text } para el resto (WhatsApp, SMS, plantillas).
function extractContent(content) {
  if (!content) return { text: '(sin contenido)' };
  if (typeof content === 'string') {
    return looksLikeHtml(content) ? { html: content } : { text: content };
  }

  // Inbound WhatsApp
  if (content?.text?.body) return { text: content.text.body };

  // Outbound — email/sms tienen body renderizado
  if (content?.body) {
    return looksLikeHtml(content.body) ? { html: content.body } : { text: content.body };
  }

  // Outbound — WhatsApp template: mostrar plantilla + variables
  if (content?.type === 'template') {
    const vars = content.variables ?? {};
    const varsStr = Object.entries(vars).map(([k, v]) => `${k}: ${v}`).join(' · ');
    return { text: `📤 ${content.clave ?? content.templateName}${varsStr ? `\n${varsStr}` : ''}` };
  }

  return { text: JSON.stringify(content).slice(0, 80) };
}

// Renderiza HTML de email sanitizado dentro de un iframe aislado (sin scripts,
// estilos del email no se filtran a la app). Ajusta la altura al contenido.
function EmailFrame({ html }) {
  const frameRef = useRef(null);
  const [height, setHeight] = useState(120);
  const frameId = useRef(`email-${Math.random().toString(36).slice(2)}`).current;

  const clean = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['style'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick'],
  });

  // El iframe está aislado (sandbox sin allow-same-origin), así que no podemos
  // leer su document desde el padre. En su lugar, un script dentro del iframe
  // mide su alto y lo envía por postMessage.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.data?.type === 'email-height' && e.data?.id === frameId) {
        setHeight(e.data.height + 16);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frameId]);

  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank">
<style>body{margin:0;font-family:system-ui,sans-serif;font-size:14px;color:#222;word-break:break-word}img{max-width:100%;height:auto}</style>
</head><body>${clean}
<script>
(function(){
  function report(){
    parent.postMessage({type:'email-height',id:${JSON.stringify(frameId)},height:document.body.scrollHeight},'*');
  }
  window.addEventListener('load',report);
  new ResizeObserver(report).observe(document.body);
  Array.from(document.images).forEach(function(img){img.addEventListener('load',report);});
  report();
})();
<\/script>
</body></html>`;

  return (
    <iframe
      ref={frameRef}
      title="email"
      sandbox="allow-scripts allow-popups"
      srcDoc={srcDoc}
      style={{ width: '100%', height, border: 'none', background: '#fff', borderRadius: 8 }}
    />
  );
}

// Tarjeta de email colapsable: cabecera clicable + iframe con el HTML.
function EmailCard({ html, outbound, fromAddress, createdAt }) {
  const [open, setOpen] = useState(false);
  const fecha = new Date(createdAt).toLocaleString('es-CL');

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 680,
        margin: '0 auto',
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        overflow: 'hidden',
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          padding: '10px 12px',
          background: '#f7f7f7',
          border: 'none',
          borderBottom: open ? '1px solid #eee' : 'none',
          outline: 'none',
          cursor: 'pointer',
          fontSize: 12,
          color: '#555',
          textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: '#aaa', fontSize: 10 }}>{open ? '▼' : '▶'}</span>
          {outbound ? 'Enviado' : 'Recibido'}{fromAddress ? ` · ${fromAddress}` : ''}
        </span>
        <span style={{ color: '#aaa' }}>{fecha}</span>
      </button>
      {open && <EmailFrame html={html} />}
    </div>
  );
}

export default function ConversacionDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mensajes, setMensajes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    getMensajesDeConversacion(id)
      .then(setMensajes)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    const notifAvailable = typeof Notification !== 'undefined';
    if (notifAvailable && Notification.permission === 'default') Notification.requestPermission();

    const channel = supabase
      .channel(`conversacion-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'messaging',
          table: 'mensajes',
          filter: `conversacion_id=eq.${id}`,
        },
        (payload) => {
          setMensajes((prev) => [...prev, payload.new]);
          if (notifAvailable && document.visibilityState === 'hidden' && Notification.permission === 'granted') {
            const preview = extractContent(payload.new.content);
            new Notification(preview.html ? 'Nuevo email' : 'Nuevo mensaje', {
              body: preview.html ? '📧 Tienes un nuevo correo' : preview.text,
              icon: '/favicons/android-chrome-192x192.png',
            });
          }
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensajes]);

  if (loading) return <p style={{ padding: 24 }}>Cargando…</p>;
  if (error) return <p style={{ padding: 24, color: 'red' }}>Error: {error}</p>;

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <button onClick={() => navigate('/conversaciones')} style={{ marginBottom: 16 }}>
        ← Volver
      </button>
      <h3>Conversación</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {mensajes.map((m) => {
          const parsed = extractContent(m.content);
          const outbound = m.direction === 'outbound';

          // Emails: tarjeta de correo colapsable, sin estética de chat.
          if (parsed.html) {
            return (
              <EmailCard
                key={m.id}
                html={parsed.html}
                outbound={outbound}
                fromAddress={m.from_address}
                createdAt={m.created_at}
              />
            );
          }

          // WhatsApp / SMS / plantillas: burbuja de chat.
          return (
            <div
              key={m.id}
              style={{
                alignSelf: outbound ? 'flex-end' : 'flex-start',
                maxWidth: '75%',
                padding: '8px 12px',
                borderRadius: 12,
                background: outbound ? '#dcf8c6' : '#f0f0f0',
                fontSize: 14,
              }}
            >
              <div style={{ whiteSpace: 'pre-wrap' }}>{parsed.text}</div>
              <div style={{ fontSize: 10, color: '#aaa', marginTop: 4, textAlign: 'right' }}>
                {new Date(m.created_at).toLocaleTimeString('es-CL')}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
